import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { CATEGORIES, IMAGES } from "../data/content";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";

export default function Gallery() {
  const [projects, setProjects] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => { api.get("/projects", { params: { category: cat } }).then((r) => setProjects(r.data)).catch(() => {}); }, [cat]);

  return (
    <div className="pt-28 pb-24 min-h-screen noise">
      <Seo title="Court Transformations Gallery | Speedy Resurfacing Sydney"
           description="Real before & after court resurfacing projects across Sydney and NSW — basketball, netball, school and custom courts by Team Speedy."
           path="/gallery" />
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal>
          <Overline>The Court Archive</Overline>
          <h1 className="mt-5 font-heading font-black text-5xl md:text-7xl tracking-tighter">REAL COURTS. REAL WORK.</h1>
          <p className="mt-5 max-w-xl text-zinc-400">A growing archive of Speedy court transformations. Filter by court type.</p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c}`}
              className={`rounded-sm border px-4 py-2.5 min-h-[44px] text-sm capitalize transition-colors ${cat === c ? "border-neon text-neon" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.05}>
              <Link to={`/gallery/${p.id}`} data-testid={`project-${p.id}`}
                className="group block overflow-hidden rounded-lg border border-white/10 bg-obsidian-800 hover:border-neon/40 transition-colors">
                <div className="relative h-60 overflow-hidden">
                  <img {...responsive(p.after_image || IMAGES.speedyBball, "(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw", [480, 800])} loading="lazy" decoding="async" alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 to-transparent" />
                  {p.needs_real_media && (
                    <span className="absolute top-3 left-3 rounded-sm bg-gold/90 px-2 py-1 font-mono text-[9px] font-bold text-obsidian">REAL SPEEDY IMAGE REQUIRED</span>
                  )}
                  <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-widest text-neon capitalize">{p.category}</span>
                </div>
                <div className="p-6 flex items-start justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-lg group-hover:text-neon transition-colors">{p.title}</h3>
                    <p className="text-sm text-zinc-500">{p.location}</p>
                  </div>
                  <ArrowUpRight className="text-zinc-700 group-hover:text-neon transition-colors" size={20} />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        {projects.length === 0 && <p className="mt-16 text-center text-zinc-500 font-mono text-sm">No projects in this category yet.</p>}
      </div>
    </div>
  );
}
